import React, { useEffect, useState, useCallback } from 'react';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, ComposedChart,
} from 'recharts';
import { getRevenue, parseReport } from '../api';
import ChartCard from '../components/ChartCard';
import ChartTooltip from '../components/ChartTooltip';

const COLORS = ['#38bdf8','#818cf8','#34d399','#f472b6','#fb923c','#fbbf24','#a78bfa','#22d3ee'];

// ── helpers ─────────────────────────────────────────────────────────────
function fmtMoney(n) {
  if (!n && n !== 0) return '$0';
  if (n >= 1e6) return '$' + (n / 1e6).toFixed(2) + 'M';
  if (n >= 1e3) return '$' + (n / 1e3).toFixed(1) + 'K';
  return '$' + n.toFixed(2);
}
function fmtNum(n) {
  if (!n && n !== 0) return '0';
  if (n >= 1e6) return (n / 1e6).toFixed(1) + 'M';
  if (n >= 1e3) return (n / 1e3).toFixed(1) + 'K';
  return Math.round(n).toString();
}
function fmtDate(d) {
  if (!d || d.length < 8) return d;
  return `${d.slice(4,6)}/${d.slice(6,8)}`;
}

// ── KPI mini card ────────────────────────────────────────────────────────
function RevenueKpi({ label, value, sub, icon, color }) {
  const c = color || 'var(--accent)';
  return (
    <div style={{ ...kpiStyles.card, boxShadow: `0 0 0 1px ${c}22` }}>
      <div style={{ ...kpiStyles.iconBox, background: c + '18', color: c }}>{icon}</div>
      <div style={kpiStyles.right}>
        <div style={kpiStyles.value}>{value}</div>
        <div style={kpiStyles.label}>{label}</div>
        {sub && <div style={kpiStyles.sub}>{sub}</div>}
      </div>
      <div style={{ ...kpiStyles.bar, background: c }} />
    </div>
  );
}
const kpiStyles = {
  card: {
    background: 'var(--surface)', border: '1px solid var(--border)',
    borderRadius: 14, padding: '16px 18px', display: 'flex',
    alignItems: 'center', gap: 14, position: 'relative', overflow: 'hidden',
    transition: 'background 0.25s',
  },
  iconBox: { width: 44, height: 44, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 },
  right:  { flex: 1, minWidth: 0 },
  value:  { fontSize: 22, fontWeight: 800, color: 'var(--text)', letterSpacing: '-0.5px', lineHeight: 1.1 },
  label:  { fontSize: 11, color: 'var(--text2)', marginTop: 4, fontWeight: 500 },
  sub:    { fontSize: 10, color: 'var(--text3)', marginTop: 2 },
  bar:    { position: 'absolute', bottom: 0, left: 0, right: 0, height: 3, opacity: 0.5 },
};

// ── Pie label ─────────────────────────────────────────────────────────────
const PieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  const R = Math.PI / 180;
  const r = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + r * Math.cos(-midAngle * R);
  const y = cy + r * Math.sin(-midAngle * R);
  if (percent < 0.05) return null;
  return <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight={700}>{(percent * 100).toFixed(0)}%</text>;
};

// ── Main Component ────────────────────────────────────────────────────────
export default function Revenue({ propertyId, startDate, endDate }) {
  const [daily,   setDaily]   = useState([]);
  const [items,   setItems]   = useState([]);
  const [country, setCountry] = useState([]);
  const [channel, setChannel] = useState([]);
  const [kpis,    setKpis]    = useState({});
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const load = useCallback(async () => {
    if (!propertyId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await getRevenue(propertyId, startDate, endDate);
      const d   = res.data;

      // daily rows sorted
      const dailyRows = parseReport(d.daily)
        .sort((a, b) => (a.date||'').localeCompare(b.date||''))
        .map(r => ({
          ...r,
          date:              fmtDate(r.date),
          purchaseRevenue:   +r.purchaseRevenue   || 0,
          totalAdRevenue:    +r.totalAdRevenue     || 0,
          transactions:      +r.transactions       || 0,
          totalRevenue:      (+r.purchaseRevenue||0) + (+r.totalAdRevenue||0),
        }));
      setDaily(dailyRows);

      // KPI aggregates
      const tot = dailyRows.reduce((acc, r) => ({
        purchaseRevenue:        (acc.purchaseRevenue||0)        + r.purchaseRevenue,
        totalAdRevenue:         (acc.totalAdRevenue||0)         + r.totalAdRevenue,
        transactions:           (acc.transactions||0)           + r.transactions,
        ecommercePurchases:     (acc.ecommercePurchases||0)     + (r.ecommercePurchases||0),
        averagePurchaseRevenue: (acc.averagePurchaseRevenue||0) + (r.averagePurchaseRevenue||0),
        _count: (acc._count||0) + 1,
      }), {});
      tot.totalRevenue = tot.purchaseRevenue + tot.totalAdRevenue;
      tot.avgPurchaseRevenue = tot._count ? tot.averagePurchaseRevenue / tot._count : 0;
      setKpis(tot);

      // items
      const itemRows = parseReport(d.items)
        .filter(r => r.itemName && r.itemName !== '(not set)')
        .sort((a, b) => b.itemRevenue - a.itemRevenue)
        .slice(0, 15);
      setItems(itemRows);

      // country
      const ctryRows = parseReport(d.country)
        .map(r => ({
          ...r,
          totalRev: (+r.purchaseRevenue||0) + (+r.totalAdRevenue||0),
        }))
        .sort((a, b) => b.totalRev - a.totalRev)
        .slice(0, 12);
      setCountry(ctryRows);

      // channel
      const chRows = parseReport(d.channel)
        .map(r => ({
          ...r,
          name:     r.sessionDefaultChannelGroup,
          totalRev: (+r.purchaseRevenue||0) + (+r.totalAdRevenue||0),
        }))
        .sort((a, b) => b.totalRev - a.totalRev);
      setChannel(chRows);

    } catch (e) {
      console.error(e);
      setError(e.response?.data?.error || e.message);
    }
    setLoading(false);
  }, [propertyId, startDate, endDate]);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 320, gap: 14 }}>
      <div style={{ fontSize: 40, color: 'var(--accent)', animation: 'spin 1s linear infinite' }}>◈</div>
      <div style={{ color: 'var(--text3)', fontSize: 13 }}>Loading revenue data...</div>
    </div>
  );

  if (error) return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--danger)33', borderRadius: 14, padding: 32, textAlign: 'center' }}>
      <div style={{ fontSize: 32, marginBottom: 12 }}>⚠️</div>
      <div style={{ color: 'var(--danger)', fontWeight: 600, fontSize: 14, marginBottom: 8 }}>Revenue Data Unavailable</div>
      <div style={{ color: 'var(--text3)', fontSize: 12, maxWidth: 420, margin: '0 auto', lineHeight: 1.7 }}>
        {error.includes('PERMISSION_DENIED') || error.includes('400')
          ? 'This property may not have e-commerce or ad monetization enabled, or the GA4 account does not have revenue metrics. Enable Google Ads or In-App Purchases in your GA4 property to see revenue data.'
          : error}
      </div>
    </div>
  );

  const adRevShare  = kpis.totalRevenue ? (kpis.totalAdRevenue  / kpis.totalRevenue) * 100 : 0;
  const iapRevShare = kpis.totalRevenue ? (kpis.purchaseRevenue / kpis.totalRevenue) * 100 : 0;

  const revPieDat = [
    { name: 'Ad Revenue',       value: kpis.totalAdRevenue   || 0 },
    { name: 'Purchase Revenue', value: kpis.purchaseRevenue  || 0 },
  ].filter(d => d.value > 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* ── KPI Cards ─────────────────────────────── */}
      <div style={styles.kpiGrid}>
        <RevenueKpi label="Total Revenue"       value={fmtMoney(kpis.totalRevenue)}      icon="💰" color="#f472b6" sub="Purchase + Ad Revenue" />
        <RevenueKpi label="Purchase Revenue"    value={fmtMoney(kpis.purchaseRevenue)}   icon="🛒" color="#38bdf8" sub={`${iapRevShare.toFixed(1)}% of total`} />
        <RevenueKpi label="Ad Revenue"          value={fmtMoney(kpis.totalAdRevenue)}    icon="📢" color="#818cf8" sub={`${adRevShare.toFixed(1)}% of total`} />
        <RevenueKpi label="Transactions"        value={fmtNum(kpis.transactions)}        icon="🧾" color="#34d399" />
        <RevenueKpi label="Avg Purchase Value"  value={fmtMoney(kpis.avgPurchaseRevenue)}icon="📈" color="#fb923c" sub="per transaction" />
        <RevenueKpi label="IAP Purchases"       value={fmtNum(kpis.ecommercePurchases)}  icon="📦" color="#fbbf24" sub="in-app purchases" />
      </div>

      {/* ── Revenue Split Banner ─────────────────── */}
      {kpis.totalRevenue > 0 && (
        <div style={styles.splitBanner}>
          <div style={styles.splitLabel}>Revenue Split</div>
          <div style={styles.splitBar}>
            <div style={{ ...styles.splitFill, width: `${iapRevShare}%`, background: 'linear-gradient(90deg,#38bdf8,#818cf8)' }} title={`Purchase: ${iapRevShare.toFixed(1)}%`} />
            <div style={{ ...styles.splitFill, width: `${adRevShare}%`, background: 'linear-gradient(90deg,#f472b6,#fb923c)' }} title={`Ads: ${adRevShare.toFixed(1)}%`} />
          </div>
          <div style={styles.splitLegend}>
            <span style={{ color: '#38bdf8' }}>● IAP {iapRevShare.toFixed(1)}%</span>
            <span style={{ color: '#f472b6' }}>● Ads {adRevShare.toFixed(1)}%</span>
          </div>
        </div>
      )}

      {/* ── Daily Revenue Trend ──────────────────── */}
      <ChartCard title="Daily Revenue Trend" subtitle="Purchase revenue + Ad revenue over time">
        <ResponsiveContainer width="100%" height={280}>
          <ComposedChart data={daily} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
            <defs>
              <linearGradient id="iapGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#38bdf8" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#38bdf8" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="adGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#f472b6" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#f472b6" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis dataKey="date" tick={{ fill: 'var(--text3)', fontSize: 10 }} />
            <YAxis tick={{ fill: 'var(--text3)', fontSize: 10 }} tickFormatter={v => '$' + (v >= 1000 ? (v/1000).toFixed(0)+'K' : v)} />
            <Tooltip content={<ChartTooltip formatter={(v) => fmtMoney(v)} />} />
            <Legend wrapperStyle={{ fontSize: 12, color: 'var(--text2)' }} />
            <Area type="monotone" dataKey="purchaseRevenue" name="Purchase Revenue" stroke="#38bdf8" fill="url(#iapGrad)" strokeWidth={2} dot={false} />
            <Area type="monotone" dataKey="totalAdRevenue"  name="Ad Revenue"       stroke="#f472b6" fill="url(#adGrad)"  strokeWidth={2} dot={false} />
            <Line  type="monotone" dataKey="totalRevenue"   name="Total Revenue"    stroke="#fbbf24" strokeWidth={2.5} dot={false} strokeDasharray="4 2" />
          </ComposedChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* ── 2-col row ────────────────────────────── */}
      <div style={styles.twoCol}>

        {/* Revenue Split Pie */}
        <ChartCard title="Revenue Source Split" subtitle="Ad vs Purchase breakdown">
          {revPieDat.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={revPieDat} cx="50%" cy="50%" innerRadius={65} outerRadius={110}
                  dataKey="value" nameKey="name" labelLine={false} label={PieLabel}>
                  {revPieDat.map((_, i) => <Cell key={i} fill={['#38bdf8','#f472b6'][i]} />)}
                </Pie>
                <Tooltip content={<ChartTooltip formatter={fmtMoney} />} />
                <Legend wrapperStyle={{ fontSize: 12, color: 'var(--text2)' }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div style={styles.noData}>No split data available</div>
          )}
        </ChartCard>

        {/* Daily Transactions */}
        <ChartCard title="Daily Transactions" subtitle="Purchase count per day">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={daily} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="date" tick={{ fill: 'var(--text3)', fontSize: 10 }} />
              <YAxis tick={{ fill: 'var(--text3)', fontSize: 10 }} />
              <Tooltip content={<ChartTooltip />} />
              <Bar dataKey="transactions" name="Transactions" radius={[4,4,0,0]}>
                {daily.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* ── Revenue by Channel ───────────────────── */}
      {channel.length > 0 && (
        <ChartCard title="Revenue by Acquisition Channel" subtitle="Which channel drives the most revenue">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={channel} margin={{ top: 5, right: 10, left: 0, bottom: 30 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="name" tick={{ fill: 'var(--text3)', fontSize: 10, angle: -20, textAnchor: 'end' }} />
              <YAxis tick={{ fill: 'var(--text3)', fontSize: 10 }} tickFormatter={v => '$'+(v>=1000?(v/1000).toFixed(0)+'K':v)} />
              <Tooltip content={<ChartTooltip formatter={fmtMoney} />} />
              <Legend wrapperStyle={{ fontSize: 12, color: 'var(--text2)' }} />
              <Bar dataKey="purchaseRevenue" name="Purchase Rev" fill="#38bdf8" radius={[4,4,0,0]} stackId="a" />
              <Bar dataKey="totalAdRevenue"  name="Ad Rev"       fill="#f472b6" radius={[4,4,0,0]} stackId="a" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      )}

      {/* ── Revenue by Country ──────────────────── */}
      {country.length > 0 && (
        <ChartCard title="Revenue by Country" subtitle="Geographic revenue distribution">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={country.slice(0,10)} layout="vertical" margin={{ top: 0, right: 30, left: 70, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
              <XAxis type="number" tick={{ fill: 'var(--text3)', fontSize: 10 }} tickFormatter={v => '$'+(v>=1000?(v/1000).toFixed(0)+'K':v)} />
              <YAxis type="category" dataKey="country" tick={{ fill: 'var(--text2)', fontSize: 11 }} width={65} />
              <Tooltip content={<ChartTooltip formatter={fmtMoney} />} />
              <Legend wrapperStyle={{ fontSize: 12, color: 'var(--text2)' }} />
              <Bar dataKey="purchaseRevenue" name="Purchase Rev" fill="#38bdf8" radius={[0,3,3,0]} stackId="b" />
              <Bar dataKey="totalAdRevenue"  name="Ad Rev"       fill="#f472b6" radius={[0,3,3,0]} stackId="b" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      )}

      {/* ── Top Items ───────────────────────────── */}
      {items.length > 0 && (
        <ChartCard title="Top Revenue Items / Products" subtitle="Best performing items by revenue">
          <div style={styles.itemsTable}>
            <div style={styles.itemHead}>
              {['#','Item Name','Item Revenue','Units Sold','Avg Rev / User'].map(h => <span key={h}>{h}</span>)}
            </div>
            {items.map((r, i) => (
              <div key={i} style={styles.itemRow}>
                <span style={{ color: 'var(--text3)', fontFamily: 'Space Mono, monospace', fontSize: 11 }}>{i+1}</span>
                <span style={{ color: COLORS[i%COLORS.length], fontWeight: 600 }}>{r.itemName}</span>
                <span style={{ color: 'var(--success)', fontWeight: 700, fontFamily: 'Space Mono, monospace' }}>{fmtMoney(r.itemRevenue)}</span>
                <span style={{ fontFamily: 'Space Mono, monospace' }}>{fmtNum(r.itemsPurchased)}</span>
                <span style={{ color: 'var(--accent2)', fontFamily: 'Space Mono, monospace' }}>{fmtMoney(r.averagePurchaseRevenuePerUser)}</span>
              </div>
            ))}
          </div>
        </ChartCard>
      )}

      {/* ── Full Country Table ──────────────────── */}
      {country.length > 0 && (
        <ChartCard title="Country Revenue Table" subtitle="All countries with revenue data">
          <div style={styles.itemsTable}>
            <div style={{ ...styles.itemHead, gridTemplateColumns: '30px 2fr 1fr 1fr 1fr 1fr' }}>
              {['#','Country','Total Rev','Purchase Rev','Ad Rev','Transactions'].map(h => <span key={h}>{h}</span>)}
            </div>
            {country.map((r, i) => (
              <div key={i} style={{ ...styles.itemRow, gridTemplateColumns: '30px 2fr 1fr 1fr 1fr 1fr' }}>
                <span style={{ color: 'var(--text3)', fontSize: 11 }}>{i+1}</span>
                <span style={{ color: 'var(--text)', fontWeight: 600 }}>{r.country}</span>
                <span style={{ color: 'var(--success)', fontWeight: 700, fontFamily: 'Space Mono, monospace' }}>{fmtMoney(r.totalRev)}</span>
                <span style={{ fontFamily: 'Space Mono, monospace', fontSize: 11 }}>{fmtMoney(r.purchaseRevenue)}</span>
                <span style={{ fontFamily: 'Space Mono, monospace', fontSize: 11 }}>{fmtMoney(r.totalAdRevenue)}</span>
                <span style={{ fontFamily: 'Space Mono, monospace' }}>{fmtNum(r.transactions)}</span>
              </div>
            ))}
          </div>
        </ChartCard>
      )}

    </div>
  );
}

const styles = {
  kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14 },
  splitBanner: {
    background: 'var(--surface)', border: '1px solid var(--border)',
    borderRadius: 12, padding: '14px 20px',
    display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
  },
  splitLabel:  { fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.08em', flexShrink: 0 },
  splitBar:    { flex: 1, height: 12, borderRadius: 6, background: 'var(--surface2)', display: 'flex', overflow: 'hidden', minWidth: 120 },
  splitFill:   { height: '100%', transition: 'width 0.5s ease' },
  splitLegend: { display: 'flex', gap: 14, fontSize: 12, fontWeight: 600, flexShrink: 0 },
  twoCol:      { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 },
  noData:      { display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200, color: 'var(--text3)', fontSize: 13 },
  itemsTable:  { display: 'flex', flexDirection: 'column' },
  itemHead:    {
    display: 'grid',
    gridTemplateColumns: '30px 2fr 1fr 1fr 1fr',
    padding: '8px 12px', fontSize: 10, color: 'var(--text3)',
    fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
    borderBottom: '1px solid var(--border)', background: 'var(--surface2)', borderRadius: 8, gap: 8,
  },
  itemRow: {
    display: 'grid',
    gridTemplateColumns: '30px 2fr 1fr 1fr 1fr',
    padding: '11px 12px', fontSize: 12, color: 'var(--text2)',
    borderBottom: '1px solid var(--border2)', gap: 8, alignItems: 'center',
  },
};
